/**
 * Admin API Service
 *
 * Provides API functions for Super Admin user and company management.
 * All endpoints require super_admin role authentication.
 */

import axios from 'axios';

// Create axios instance with base URL
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ? `${import.meta.env.VITE_API_BASE_URL}/api` : '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling and unwrapping
api.interceptors.response.use(
  (response) => {
    // Unwrap { success, data } responses
    if (response.data && response.data.success !== undefined && response.data.data !== undefined) {
      const total = response.data.total;
      const hasMore = response.data.hasMore;

      response.data = response.data.data;

      // Re-attach pagination metadata if present
      if (total !== undefined) {
        response.data = {
          data: response.data,
          total,
          hasMore,
        };
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/register')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type UserRole = 'super_admin' | 'company_admin' | 'analyst' | 'viewer' | 'user';
export type SubscriptionTier = 'free' | 'basic' | 'pro' | 'enterprise';
export type UserStatus = 'active' | 'inactive' | 'suspended' | 'pending';
export type CompanyStatus = 'active' | 'inactive' | 'suspended';
export type EntityType = 'insurance' | 'shipper' | 'broker' | 'carrier';

// RBAC Role types
export interface RBACRole {
  id: number;
  name: string;
  display_name: string;
  description: string | null;
  is_system_role: boolean;
  permissions?: RBACPermission[];
  permission_count?: number;
  user_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface RBACPermission {
  id: number;
  key: string;
  name: string;
  description: string | null;
  category: string;
  created_at?: string;
}

export interface UserPermissionOverride {
  permission_key: string;
  category: string;
  granted: boolean;
  reason: string | null;
  granted_at: string;
  granted_by: number;
  expires_at: string | null;
  granted_by_email?: string;
}

export interface UserEffectivePermission {
  permission_key: string;
  permission_category: string;
  source: 'from_role' | 'granted';
  expires_at: string | null;
}

export interface UserPermissionsResponse {
  user: {
    id: number;
    email: string;
  };
  role: {
    name: string;
    display_name: string;
  } | null;
  permissions: UserEffectivePermission[];
  overrides: UserPermissionOverride[];
  summary: {
    total_permissions: number;
    from_role: number;
    granted: number;
    revoked: number;
  };
}

export interface GrantPermissionData {
  permission_key: string;
  reason: string;
  expires_at?: string;
}

export interface RevokePermissionData {
  permission_key: string;
  reason: string;
}

export interface PermissionAuditEntry {
  id: number;
  action: 'grant' | 'revoke' | 'role_assigned' | 'role_removed';
  subject_type: 'user' | 'role' | 'company';
  subject_id: number;
  subject_email?: string;
  permission_key: string;
  performed_by: number;
  performer_email?: string;
  performer_name?: string;
  reason: string | null;
  metadata?: {
    expires_at?: string;
    old_role?: string;
    new_role?: string;
    [key: string]: unknown;
  };
  created_at: string;
}

export interface PermissionAuditQueryParams {
  user_id?: number;
  permission_key?: string;
  action?: 'grant' | 'revoke' | 'role_assigned' | 'role_removed';
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
}

export interface AdminUser {
  id: number;
  email: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  companyId?: number;
  companyName?: string;
  subscriptionTier: SubscriptionTier;
  status: UserStatus;
  lastLogin?: string;
  createdAt: string;
  updatedAt?: string;
  // RBAC fields
  roleId?: number;
  rbacRole?: string;  // RBAC role name (super_admin, company_admin, etc.)
  rbacRoleDisplay?: string;  // RBAC role display name (Super Administrator, etc.)
  permissionCount?: number;  // Number of permissions this user has
  // Phase E: User-level DOT restrictions
  allowed_dot_numbers?: number[] | null; // Array of DOT numbers user can access
}

export interface AdminCompany {
  id: number;
  name: string;
  domain?: string;
  subscriptionTier: SubscriptionTier;
  usersCount: number;
  maxUsers: number;
  status: CompanyStatus;
  createdAt: string;
  updatedAt?: string;
  billingEmail?: string;
  contactPhone?: string;
  address?: string;
  entity_type?: EntityType;
  entity_metadata?: Record<string, unknown>;
}

export interface CreateUserData {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  role: UserRole;
  companyId?: number;
  subscriptionTier: SubscriptionTier;
  status?: UserStatus;
  allowed_dot_numbers?: number[] | null; // Phase E: User-level DOT restrictions
}

export interface UpdateUserData {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  companyId?: number;
  subscriptionTier?: SubscriptionTier;
  status?: UserStatus;
  password?: string;
}

export interface CreateCompanyData {
  name: string;
  domain?: string;
  subscriptionTier: SubscriptionTier;
  maxUsers: number;
  status?: CompanyStatus;
  billingEmail?: string;
  contactPhone?: string;
  address?: string;
  entity_type?: EntityType;
  entity_metadata?: Record<string, unknown>;
}

export interface UpdateCompanyData {
  name?: string;
  domain?: string;
  subscriptionTier?: SubscriptionTier;
  maxUsers?: number;
  status?: CompanyStatus;
  billingEmail?: string;
  contactPhone?: string;
  address?: string;
}

export interface UpdateSubscriptionData {
  subscriptionTier: SubscriptionTier;
  effectiveDate?: string;
  reason?: string;
}

export interface UserQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: UserRole;
  subscriptionTier?: SubscriptionTier;
  status?: UserStatus;
  companyId?: number;
  sortBy?: 'email' | 'createdAt' | 'lastLogin' | 'companyName';
  sortOrder?: 'asc' | 'desc';
}

export interface CompanyQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  subscriptionTier?: SubscriptionTier;
  status?: CompanyStatus;
  sortBy?: 'name' | 'createdAt' | 'usersCount';
  sortOrder?: 'asc' | 'desc';
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface PlatformUsageStats {
  period: string;
  totalUsers: number;
  activeUsers: number;
  newUsers: number;
  totalCompanies: number;
  activeCompanies: number;
  apiCalls: number;
  storageUsed: number;
  revenueEstimate?: number;
  dailyUsage: Array<{
    date: string;
    activeUsers: number;
    apiCalls: number;
  }>;
  usageByTier: Array<{
    tier: SubscriptionTier;
    count: number;
    percentage: number;
  }>;
  usageByType: Array<{
    type: string;
    count: number;
  }>;
}

export interface UserUsageStats {
  userId: number;
  period: string;
  apiCalls: number;
  searches: number;
  reportsGenerated: number;
  lastActive: string;
  sessionCount: number;
  avgSessionDuration: number;
}

export interface AuditLogEntry {
  id: number;
  userId: number;
  userEmail: string;
  action: string;
  resourceType: string;
  resourceId?: number;
  details: Record<string, unknown>;
  ipAddress: string;
  userAgent?: string;
  createdAt: string;
}

export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  newUsers7d: number;
  newUsers30d: number;
  totalCompanies: number;
  activeCompanies: number;
  revenueMonthly?: number;
  revenueGrowth?: number;
  apiUsage24h: number;
  errorRate24h: number;
}

// ============================================================================
// FEATURE MANAGEMENT TYPES
// ============================================================================

export type FeatureCategory = 'core' | 'advanced' | 'export' | 'monitoring' | 'api';
export type LimitPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface AvailableFeature {
  key: string;
  name: string;
  description: string;
  category: FeatureCategory;
  is_default: boolean;
  has_limit: boolean;
  default_limit_value?: number;
  default_limit_period?: LimitPeriod;
}

export interface CompanyFeature {
  feature_key: string;
  feature_name: string;
  feature_description: string;
  category: FeatureCategory;
  is_enabled: boolean;
  has_limit: boolean;
  limit_value?: number;
  limit_period?: LimitPeriod;
  current_usage?: number;
  usage_reset_at?: string;
  enabled_at?: string;
  enabled_by?: number;
}

export interface CompanyFeaturesResponse {
  company_id: number;
  company_name: string;
  features: CompanyFeature[];
  summary: {
    total_features: number;
    enabled_features: number;
    features_by_category: Record<FeatureCategory, { total: number; enabled: number }>;
  };
}

export interface UpdateCompanyFeatureData {
  is_enabled: boolean;
  limit_value?: number;
  limit_period?: LimitPeriod;
}

export interface BatchUpdateFeatureItem {
  feature_key: string;
  is_enabled: boolean;
  limit_value?: number;
  limit_period?: LimitPeriod;
}

export interface BatchUpdateFeaturesData {
  features: BatchUpdateFeatureItem[];
}

export interface CreateCompanyFeaturesData {
  features: BatchUpdateFeatureItem[];
}

// ============================================================================
// ENTITY TYPE FEATURE TEMPLATES
// ============================================================================

/**
 * Feature templates for each entity type.
 * Defines which features should be enabled by default for each company type.
 */
export const ENTITY_TYPE_FEATURES: Record<EntityType, string[]> = {
  insurance: [
    // Full access - insurance companies get everything
    'carrier_search',
    'basic_analytics',
    'market_intelligence',
    'vin_lookup',
    'shipper_intel',  // For risk assessment
    'export_csv',
    'export_pdf',
    'bulk_export',
    'real_time_monitoring',
    'alerts',
    'api_access',
  ],
  shipper: [
    // All carrier data + own shipper data
    'carrier_search',
    'basic_analytics',
    'market_intelligence',
    'vin_lookup',
    'shipper_intel',  // Own data only (enforced by backend)
    'export_csv',
    'export_pdf',
    'bulk_export',
    'real_time_monitoring',
    'alerts',
    'api_access',
  ],
  broker: [
    // All carrier data, NO shipper features
    'carrier_search',
    'basic_analytics',
    'market_intelligence',
    'vin_lookup',
    // shipper_intel explicitly excluded
    'export_csv',
    'export_pdf',
    'bulk_export',
    'real_time_monitoring',
    'alerts',
    'api_access',
  ],
  carrier: [
    // Limited to own data only
    'basic_analytics',  // Own data
    'vin_lookup',  // Own vehicles
    'export_csv',  // Own data
    'export_pdf',  // Own data
    'real_time_monitoring',  // Own company
    'alerts',  // Own company
    // Explicitly excluded: carrier_search, market_intelligence, shipper_intel, bulk_export, api_access
  ],
};

/**
 * Entity type metadata (descriptions, icons, colors)
 */
export interface EntityTypeInfo {
  type: EntityType;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
}

export const ENTITY_TYPE_INFO: Record<EntityType, EntityTypeInfo> = {
  insurance: {
    type: 'insurance',
    label: 'Insurance Company',
    description: 'Full access to all features + DOT-level user restrictions',
    icon: '🏢',
    color: 'blue',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-700',
  },
  shipper: {
    type: 'shipper',
    label: 'Shipper Company',
    description: 'All carrier data + own shipper audit information',
    icon: '📦',
    color: 'green',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-500',
    textColor: 'text-green-700',
  },
  broker: {
    type: 'broker',
    label: 'Broker Company',
    description: 'All carrier data, no shipper analytics or data',
    icon: '🚚',
    color: 'purple',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-500',
    textColor: 'text-purple-700',
  },
  carrier: {
    type: 'carrier',
    label: 'Carrier Company',
    description: 'Access to own company data only (one DOT number)',
    icon: '🚛',
    color: 'orange',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-500',
    textColor: 'text-orange-700',
  },
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Super Admin API Service
 */
export const superAdminApi = {
  // -------------------------------------------------------------------------
  // USER MANAGEMENT
  // -------------------------------------------------------------------------

  /**
   * Get all users with pagination and filtering
   */
  getAllUsers: async (params?: UserQueryParams): Promise<PaginatedResponse<AdminUser>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.subscriptionTier) queryParams.append('subscriptionTier', params.subscriptionTier);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.companyId) queryParams.append('companyId', params.companyId.toString());
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = queryString ? `/admin/users?${queryString}` : '/admin/users';
    const response = await api.get(url);

    // Transform backend response to match frontend types
    const backendData = response.data;
    const transformedUsers = (backendData.users || backendData.data || []).map((user: any) => ({
      id: user.id,
      email: user.email,
      role: user.role as UserRole,
      companyId: user.company_id,
      companyName: user.company_name,
      subscriptionTier: (user.subscription_tier || 'free') as SubscriptionTier,
      status: (user.is_active ? 'active' : 'inactive') as UserStatus,
      lastLogin: user.last_login,
      createdAt: user.created_at,
      updatedAt: user.updated_at,
      // RBAC fields
      roleId: user.role_id,
      rbacRole: user.rbac_role,
      rbacRoleDisplay: user.rbac_role_display,
      permissionCount: user.permission_count ? parseInt(user.permission_count) : undefined,
    }));

    return {
      data: transformedUsers,
      total: backendData.pagination?.totalCount || transformedUsers.length,
      page: backendData.pagination?.page || params?.page || 1,
      limit: backendData.pagination?.limit || params?.limit || 10,
      hasMore: backendData.pagination?.hasNext || false,
    };
  },

  /**
   * Get a specific user by ID
   */
  getUserById: async (id: number): Promise<AdminUser> => {
    const { data } = await api.get<AdminUser>(`/admin/users/${id}`);
    return data;
  },

  /**
   * Create a new user
   * Transforms frontend camelCase data to backend snake_case format
   */
  createUser: async (userData: CreateUserData): Promise<AdminUser> => {
    // Map frontend subscription tiers to backend enum values
    const tierMapping: Record<SubscriptionTier, string> = {
      'free': 'bronze',
      'basic': 'silver',
      'pro': 'gold',
      'enterprise': 'platinum',
    };

    // Transform frontend data to backend format
    const backendData = {
      email: userData.email,
      password: userData.password,
      company_name: userData.companyName || userData.email.split('@')[0] + ' Company', // Required by backend
      role: userData.role,
      company_id: userData.companyId,
      subscription_tier: tierMapping[userData.subscriptionTier] || 'bronze',
      is_active: userData.status !== 'inactive' && userData.status !== 'suspended',
    };

    const { data } = await api.post<AdminUser>('/admin/users', backendData);
    return data;
  },

  /**
   * Update an existing user
   */
  updateUser: async (id: number, userData: UpdateUserData): Promise<AdminUser> => {
    const { data } = await api.put<AdminUser>(`/admin/users/${id}`, userData);
    return data;
  },

  /**
   * Delete a user
   */
  deleteUser: async (id: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.delete<{ success: boolean; message: string }>(`/admin/users/${id}`);
    return data;
  },

  /**
   * Update user subscription tier
   */
  updateUserSubscription: async (id: number, subscriptionData: UpdateSubscriptionData): Promise<AdminUser> => {
    const { data } = await api.put<AdminUser>(`/admin/users/${id}/subscription`, subscriptionData);
    return data;
  },

  /**
   * Reset user password (admin action)
   */
  resetUserPassword: async (id: number, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post<{ success: boolean; message: string }>(`/admin/users/${id}/reset-password`, { password: newPassword });
    return data;
  },

  /**
   * Suspend/unsuspend a user
   */
  toggleUserStatus: async (id: number, status: UserStatus): Promise<AdminUser> => {
    const { data } = await api.put<AdminUser>(`/admin/users/${id}/status`, { status });
    return data;
  },

  // -------------------------------------------------------------------------
  // COMPANY MANAGEMENT
  // -------------------------------------------------------------------------

  /**
   * Get all companies with pagination and filtering
   */
  getAllCompanies: async (params?: CompanyQueryParams): Promise<PaginatedResponse<AdminCompany>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.subscriptionTier) queryParams.append('subscriptionTier', params.subscriptionTier);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = queryString ? `/admin/companies?${queryString}` : '/admin/companies';
    const response = await api.get(url);

    // Transform backend response to match frontend types
    const backendData = response.data;
    const transformedCompanies = (backendData.companies || backendData.data || []).map((company: any) => ({
      id: company.id,
      name: company.name,
      domain: company.domain,
      subscriptionTier: (company.subscription_tier_id ? 'pro' : 'free') as SubscriptionTier,
      usersCount: company.user_count || 0,
      maxUsers: company.max_users || 0,
      status: (company.is_active ? 'active' : 'inactive') as CompanyStatus,
      createdAt: company.created_at,
      updatedAt: company.updated_at,
      billingEmail: company.billing_email,
      contactPhone: company.billing_phone,
      address: company.billing_address,
    }));

    return {
      data: transformedCompanies,
      total: backendData.pagination?.totalCount || transformedCompanies.length,
      page: backendData.pagination?.page || params?.page || 1,
      limit: backendData.pagination?.limit || params?.limit || 10,
      hasMore: backendData.pagination?.hasNext || false,
    };
  },

  /**
   * Get a specific company by ID
   */
  getCompanyById: async (id: number): Promise<AdminCompany> => {
    const { data } = await api.get<AdminCompany>(`/admin/companies/${id}`);
    return data;
  },

  /**
   * Create a new company
   */
  createCompany: async (companyData: CreateCompanyData): Promise<AdminCompany> => {
    const { data } = await api.post<AdminCompany>('/admin/companies', companyData);
    return data;
  },

  /**
   * Update an existing company
   */
  updateCompany: async (id: number, companyData: UpdateCompanyData): Promise<AdminCompany> => {
    const { data } = await api.put<AdminCompany>(`/admin/companies/${id}`, companyData);
    return data;
  },

  /**
   * Delete a company
   */
  deleteCompany: async (id: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.delete<{ success: boolean; message: string }>(`/admin/companies/${id}`);
    return data;
  },

  /**
   * Get users belonging to a specific company
   */
  getCompanyUsers: async (id: number, params?: { page?: number; limit?: number }): Promise<PaginatedResponse<AdminUser>> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const queryString = queryParams.toString();
    const url = queryString ? `/admin/companies/${id}/users?${queryString}` : `/admin/companies/${id}/users`;
    const { data } = await api.get<PaginatedResponse<AdminUser>>(url);

    if (Array.isArray(data)) {
      return {
        data: data as AdminUser[],
        total: data.length,
        page: params?.page || 1,
        limit: params?.limit || 10,
        hasMore: false,
      };
    }

    return data;
  },

  /**
   * Update company subscription tier
   */
  updateCompanySubscription: async (id: number, subscriptionData: UpdateSubscriptionData): Promise<AdminCompany> => {
    const { data } = await api.put<AdminCompany>(`/admin/companies/${id}/subscription`, subscriptionData);
    return data;
  },

  // -------------------------------------------------------------------------
  // PLATFORM ANALYTICS & USAGE
  // -------------------------------------------------------------------------

  /**
   * Get platform usage statistics
   */
  getPlatformUsage: async (period: '7d' | '30d' | '90d' | '1y' = '30d'): Promise<PlatformUsageStats> => {
    // Fixed: correct endpoint is /admin/usage (not /admin/analytics/usage)
    const { data } = await api.get<PlatformUsageStats>(`/admin/usage?period=${period}`);
    return data;
  },

  /**
   * Get usage statistics for a specific user
   */
  getUserUsage: async (id: number, period: '7d' | '30d' | '90d' = '30d'): Promise<UserUsageStats> => {
    // Fixed: correct endpoint is /admin/usage/users/:id
    const { data } = await api.get<UserUsageStats>(`/admin/usage/users/${id}?period=${period}`);
    return data;
  },

  /**
   * Get dashboard summary statistics
   */
  getDashboardStats: async (): Promise<DashboardStats> => {
    // Get real data from backend /admin/usage endpoint
    try {
      const usageData = await superAdminApi.getPlatformUsage('30d');

      return {
        totalUsers: usageData.totalUsers || 0,
        activeUsers: usageData.activeUsers || 0,
        newUsers7d: 0, // Would need separate query
        newUsers30d: usageData.newUsers || 0,
        totalCompanies: usageData.totalCompanies || 0,
        activeCompanies: usageData.activeCompanies || 0,
        apiUsage24h: usageData.apiCalls || 0,
        errorRate24h: 0,
      };
    } catch (error) {
      // Fallback to mock data if endpoints fail
      throw error;
    }
  },

  // -------------------------------------------------------------------------
  // AUDIT LOG
  // -------------------------------------------------------------------------

  /**
   * Get audit log entries
   */
  getAuditLog: async (params?: {
    page?: number;
    limit?: number;
    userId?: number;
    action?: string;
    resourceType?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<AuditLogEntry>> => {
    // TODO: This endpoint doesn't exist yet - return empty data for now
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.userId) queryParams.append('userId', params.userId.toString());
      if (params?.action) queryParams.append('action', params.action);
      if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
      if (params?.startDate) queryParams.append('startDate', params.startDate);
      if (params?.endDate) queryParams.append('endDate', params.endDate);

      const queryString = queryParams.toString();
      const url = queryString ? `/admin/audit-log?${queryString}` : '/admin/audit-log';
      const { data } = await api.get<PaginatedResponse<AuditLogEntry>>(url);

      if (Array.isArray(data)) {
        return {
          data: data as AuditLogEntry[],
          total: data.length,
          page: params?.page || 1,
          limit: params?.limit || 10,
          hasMore: false,
        };
      }

      return data;
    } catch (error) {
      // Endpoint doesn't exist yet - return empty data
      return {
        data: [],
        total: 0,
        page: params?.page || 1,
        limit: params?.limit || 10,
        hasMore: false,
      };
    }
  },

  /**
   * Get recent admin actions (last 10)
   */
  getRecentAdminActions: async (): Promise<AuditLogEntry[]> => {
    try {
      const { data } = await api.get<PaginatedResponse<AuditLogEntry>>('/admin/audit-log?limit=10&resourceType=admin');
      if (Array.isArray(data)) {
        return data;
      }
      return data.data || [];
    } catch (error) {
      // Endpoint doesn't exist yet - return empty array
      return [];
    }
  },

  // -------------------------------------------------------------------------
  // RBAC ROLE MANAGEMENT
  // -------------------------------------------------------------------------

  /**
   * Get all RBAC roles
   */
  getAllRoles: async (): Promise<RBACRole[]> => {
    try {
      const { data } = await api.get('/admin/roles');
      // Handle both wrapped and unwrapped responses
      if (Array.isArray(data)) {
        return data;
      }
      if (data.roles && Array.isArray(data.roles)) {
        return data.roles;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      return [];
    }
  },

  /**
   * Get RBAC role by ID with permissions
   */
  getRoleById: async (id: number): Promise<RBACRole | null> => {
    try {
      const { data } = await api.get(`/admin/roles/${id}`);
      return data.role || data || null;
    } catch (error) {
      console.error(`Failed to fetch role ${id}:`, error);
      return null;
    }
  },

  /**
   * Get user permissions by user ID with full details
   */
  getUserPermissions: async (userId: number): Promise<UserPermissionsResponse> => {
    const { data } = await api.get(`/admin/permissions/users/${userId}`);
    return data;
  },

  /**
   * Get all available permissions (for dropdowns)
   */
  getAllPermissions: async (): Promise<RBACPermission[]> => {
    const { data } = await api.get('/admin/permissions');
    return data.permissions || [];
  },

  /**
   * Get all available permissions grouped by category
   */
  getAllPermissionsGrouped: async (): Promise<Record<string, RBACPermission[]>> => {
    const { data } = await api.get('/admin/permissions');
    return data.grouped_by_category || {};
  },

  /**
   * Grant a permission to a user
   */
  grantPermission: async (userId: number, permissionData: GrantPermissionData): Promise<void> => {
    await api.post(`/admin/permissions/users/${userId}/grant`, permissionData);
  },

  /**
   * Revoke a permission from a user
   */
  revokePermission: async (userId: number, permissionData: RevokePermissionData): Promise<void> => {
    await api.post(`/admin/permissions/users/${userId}/revoke`, permissionData);
  },

  /**
   * Remove a permission override (restore to role default)
   */
  removePermissionOverride: async (userId: number, permissionKey: string): Promise<void> => {
    await api.delete(`/admin/permissions/users/${userId}/${permissionKey}`);
  },

  /**
   * Get permission audit log
   */
  getPermissionAuditLog: async (params?: PermissionAuditQueryParams): Promise<PaginatedResponse<PermissionAuditEntry>> => {
    const queryParams = new URLSearchParams();
    if (params?.user_id) queryParams.append('subject_id', params.user_id.toString());
    if (params?.permission_key) queryParams.append('permission_key', params.permission_key);
    if (params?.action) queryParams.append('action', params.action);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);

    // Backend uses limit/offset, not page
    const page = params?.page || 1;
    const limit = params?.limit || 50;
    const offset = (page - 1) * limit;
    queryParams.append('limit', limit.toString());
    queryParams.append('offset', offset.toString());

    const queryString = queryParams.toString();
    const url = `/admin/permissions/audit?${queryString}`;
    const { data } = await api.get(url);

    // Backend returns { success, audit_log, pagination }
    return {
      data: data.audit_log || [],
      total: data.pagination?.total || 0,
      page: page,
      limit: limit,
      hasMore: data.pagination?.has_more || false,
    };
  },

  // -------------------------------------------------------------------------
  // FEATURE MANAGEMENT
  // -------------------------------------------------------------------------

  /**
   * Get all available features
   */
  getAvailableFeatures: async (): Promise<AvailableFeature[]> => {
    try {
      const { data } = await api.get('/admin/features/available');
      return data.features || data || [];
    } catch (error) {
      console.error('Failed to fetch available features:', error);
      // Return mock data for development
      return [
        { key: 'carrier_search', name: 'Carrier Search', description: 'Search and view carrier information', category: 'core', is_default: true, has_limit: false },
        { key: 'basic_analytics', name: 'Basic Analytics', description: 'View basic analytics and reports', category: 'core', is_default: true, has_limit: false },
        { key: 'market_intelligence', name: 'Market Intelligence', description: 'Access market trends and competitive analysis', category: 'advanced', is_default: false, has_limit: false },
        { key: 'vin_lookup', name: 'VIN Lookup', description: 'Look up vehicle information by VIN', category: 'advanced', is_default: false, has_limit: true, default_limit_value: 100, default_limit_period: 'monthly' },
        { key: 'shipper_intel', name: 'Shipper Intelligence', description: 'Access shipper information and analysis', category: 'advanced', is_default: false, has_limit: false },
        { key: 'export_csv', name: 'CSV Export', description: 'Export data to CSV format', category: 'export', is_default: false, has_limit: true, default_limit_value: 50, default_limit_period: 'daily' },
        { key: 'export_pdf', name: 'PDF Export', description: 'Export reports to PDF format', category: 'export', is_default: false, has_limit: true, default_limit_value: 20, default_limit_period: 'daily' },
        { key: 'bulk_export', name: 'Bulk Export', description: 'Export large datasets', category: 'export', is_default: false, has_limit: true, default_limit_value: 5, default_limit_period: 'weekly' },
        { key: 'real_time_monitoring', name: 'Real-Time Monitoring', description: 'Monitor carriers in real-time', category: 'monitoring', is_default: false, has_limit: false },
        { key: 'alerts', name: 'Alerts', description: 'Receive alerts and notifications', category: 'monitoring', is_default: false, has_limit: true, default_limit_value: 100, default_limit_period: 'daily' },
        { key: 'api_access', name: 'API Access', description: 'Access to REST API endpoints', category: 'api', is_default: false, has_limit: true, default_limit_value: 1000, default_limit_period: 'daily' },
      ];
    }
  },

  /**
   * Get features for a specific company
   */
  getCompanyFeatures: async (companyId: number): Promise<CompanyFeaturesResponse> => {
    try {
      const { data } = await api.get(`/admin/features/companies/${companyId}`);
      return data;
    } catch (error) {
      console.error(`Failed to fetch features for company ${companyId}:`, error);
      // Return mock data for development
      return {
        company_id: companyId,
        company_name: 'Company',
        features: [
          { feature_key: 'carrier_search', feature_name: 'Carrier Search', feature_description: 'Search and view carrier information', category: 'core', is_enabled: true, has_limit: false },
          { feature_key: 'basic_analytics', feature_name: 'Basic Analytics', feature_description: 'View basic analytics and reports', category: 'core', is_enabled: true, has_limit: false },
          { feature_key: 'market_intelligence', feature_name: 'Market Intelligence', feature_description: 'Access market trends', category: 'advanced', is_enabled: false, has_limit: false },
          { feature_key: 'export_csv', feature_name: 'CSV Export', feature_description: 'Export data to CSV', category: 'export', is_enabled: true, has_limit: true, limit_value: 50, limit_period: 'daily', current_usage: 12 },
        ],
        summary: {
          total_features: 11,
          enabled_features: 3,
          features_by_category: {
            core: { total: 2, enabled: 2 },
            advanced: { total: 3, enabled: 0 },
            export: { total: 3, enabled: 1 },
            monitoring: { total: 2, enabled: 0 },
            api: { total: 1, enabled: 0 },
          },
        },
      };
    }
  },

  /**
   * Update a single feature for a company
   */
  updateCompanyFeature: async (
    companyId: number,
    featureKey: string,
    data: UpdateCompanyFeatureData
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.put(`/admin/features/companies/${companyId}/${featureKey}`, data);
    return response.data;
  },

  /**
   * Batch update features for a company
   */
  batchUpdateCompanyFeatures: async (
    companyId: number,
    featuresData: BatchUpdateFeaturesData
  ): Promise<{ success: boolean; message: string; updated_count: number }> => {
    const { data } = await api.post(`/admin/features/companies/${companyId}/batch`, featuresData);
    return data;
  },

  /**
   * Initialize features for a new company
   */
  initializeCompanyFeatures: async (
    companyId: number,
    featuresData: CreateCompanyFeaturesData
  ): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post(`/admin/features/companies/${companyId}/initialize`, featuresData);
    return data;
  },
};

export default superAdminApi;

// Registration Requests Admin APIs
export interface RegistrationRequest {
  id: number;
  email: string;
  role: string;
  company_name: string;
  dot_number: number | null;
  mc_number: string | null;
  tax_id: string | null;
  phone: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
  updated_at: string;
}

export const registrationAdminApi = {
  getPendingCount: async (): Promise<number> => {
    const { data } = await api.get('/registration/requests/pending-count');
    return data.data.count;
  },

  listRequests: async (params?: {
    status?: 'pending' | 'approved' | 'rejected';
    limit?: number;
    offset?: number;
  }): Promise<{ requests: RegistrationRequest[]; total: number }> => {
    const { data } = await api.get('/registration/requests', { params });
    return data.data;
  },

  getRequest: async (id: number): Promise<{ request: RegistrationRequest; history: any[] }> => {
    const { data } = await api.get(`/registration/requests/${id}`);
    return data.data;
  },

  approveRequest: async (id: number, notes?: string): Promise<{ user_id: number; request: RegistrationRequest }> => {
    const { data } = await api.post(`/registration/requests/${id}/approve`, { notes });
    return data.data;
  },

  rejectRequest: async (id: number, notes?: string): Promise<{ request: RegistrationRequest }> => {
    const { data } = await api.post(`/registration/requests/${id}/reject`, { notes });
    return data.data;
  },
};
